import type { InteractionBlockStore } from "../interaction-block/InteractionBlockStore";
import type { HapticsService } from "../haptics/HapticsService";
import type { SystemModalConfig, SystemModalOutcome } from "./SystemModalTypes";
import type { SystemModalPort } from "./SystemModalPort";

interface Pending {
  config: SystemModalConfig;
  resolve: (outcome: SystemModalOutcome) => void;
}

/**
 * The system modal: `show` resolves with the button the player pressed, and a modal requested
 * while one is open waits its turn instead of replacing it (decision, 2026-09-13). One interaction
 * block covers the whole run of queued modals; a teardown resolves everything as dismissed.
 */
export class SystemModalPresenter {
  private current: Pending | null = null;
  private readonly queue: Pending[] = [];

  public constructor(
    private readonly panel: SystemModalPort,
    private readonly interactionBlockStore: InteractionBlockStore,
    private readonly hapticsService: HapticsService,
  ) {
    this.panel.bind({
      onPrimary: () => this.settle("primary"),
      onSecondary: () => this.settle("secondary"),
      onBackdrop: () => {
        if (this.current && this.current.config.dismissible !== false) this.settle("dismissed");
      },
    });
  }

  public start(): void {
    this.stop();
  }

  /** Closes whatever is open and resolves it and every queued request as dismissed. */
  public stop(): void {
    const pending = [...(this.current ? [this.current] : []), ...this.queue.splice(0)];
    this.current = null;
    this.panel.setOpen(false);
    this.interactionBlockStore.remove("modal_popup");
    for (const p of pending) p.resolve("dismissed");
  }

  /** Shows the modal, or queues it behind the open one; resolves with the outcome. */
  public show(config: SystemModalConfig): Promise<SystemModalOutcome> {
    return new Promise<SystemModalOutcome>((resolve) => {
      const pending = { config, resolve };
      if (this.current) {
        this.queue.push(pending);
        return;
      }
      this.present(pending);
    });
  }

  /** Closes the open modal as dismissed (the next queued one shows). */
  public close(): void {
    if (this.current) this.settle("dismissed");
  }

  public isOpen(): boolean {
    return this.current !== null;
  }

  private present(pending: Pending): void {
    const { config } = pending;
    this.current = pending;
    this.panel.setTitle(config.title);
    this.panel.setMessage(config.message);
    this.panel.setPrimaryButtonText(config.primaryButtonText);
    this.panel.setSecondaryVisible(!!config.secondaryButtonText);
    if (config.secondaryButtonText) this.panel.setSecondaryButtonText(config.secondaryButtonText);
    this.panel.setBackdropDismissEnabled(config.dismissible ?? true);
    this.panel.setOpen(true);
    const cue = config.cue ?? "failure";
    if (cue !== "none") this.hapticsService.play(cue);
    this.interactionBlockStore.add("modal_popup");
  }

  private settle(outcome: SystemModalOutcome): void {
    const done = this.current;
    if (!done) return;
    const next = this.queue.shift() ?? null;
    if (next) {
      this.present(next);
    } else {
      this.current = null;
      this.panel.setOpen(false);
      this.interactionBlockStore.remove("modal_popup");
    }
    done.resolve(outcome);
  }
}
